/**
 * Queue a staff invite. The auth.admin.inviteUserByEmail call lives in the
 * Trigger.dev `staff-invite` task (service role); the trigger-task Edge
 * Function enforces that the caller is a superadmin and injects the
 * inviter's identity — the browser never chooses those fields.
 *
 * Exists because public signup is disabled (2026-09-19): a new staff member
 * can no longer self-create a login with a magic link.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { StaffRole } from '@pazportal/types';
import { invokeTriggerTask } from '../lib/triggerTask';

export interface StaffInviteParams {
  email: string;
  full_name?: string;
  role: StaffRole;
  reason: string;
}

export function useStaffInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: StaffInviteParams) => {
      const email = params.email.trim().toLowerCase();
      return invokeTriggerTask(
        'staff-invite',
        { ...params, email },
        { idempotencyKey: `staff-invite-${crypto.randomUUID()}` },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-staff-users'] });
    },
  });
}
